import { useEffect, useMemo, useState } from 'react';
import {
  curatedJetBrainsPlugins,
  JETBRAINS_VENDOR_PLUGINS_ENDPOINT,
  normalizeMarketplacePlugins
} from '../data/jetbrainsPlugins';
import type { JetBrainsPlugin } from '../data/jetbrainsPlugins';

type PluginDataSource = 'curated' | 'live';

const getPluginUrl = (plugin: JetBrainsPlugin) => `https://plugins.jetbrains.com${plugin.link}`;

export function DynamicPluginGrid() {
  const [plugins, setPlugins] = useState<JetBrainsPlugin[]>(curatedJetBrainsPlugins);
  const [dataSource, setDataSource] = useState<PluginDataSource>('curated');

  useEffect(() => {
    let cancelled = false;

    const fetchPlugins = async () => {
      try {
        const response = await fetch(JETBRAINS_VENDOR_PLUGINS_ENDPOINT, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`JetBrains Marketplace responded with ${response.status}`);
        }

        const data = await response.json();
        const livePlugins = normalizeMarketplacePlugins(data);

        if (!cancelled && livePlugins.length >= curatedJetBrainsPlugins.length) {
          setPlugins(livePlugins);
          setDataSource('live');
        }
      } catch (error) {
        console.warn('Using curated JetBrains plugin data because live marketplace data is unavailable.', error);
      }
    };

    fetchPlugins();

    return () => {
      cancelled = true;
    };
  }, []);

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'JetBrains Plugins by Jakub Jirak',
    description: 'Professional IDE plugins for testing, accessibility, security analysis, and developer productivity',
    numberOfItems: plugins.length,
    itemListElement: plugins.map((plugin, index) => ({
      '@type': 'SoftwareApplication',
      position: index + 1,
      name: plugin.name,
      applicationCategory: 'DeveloperApplication',
      description: plugin.preview,
      url: getPluginUrl(plugin),
      interactionStatistic: {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/DownloadAction',
        userInteractionCount: plugin.downloads
      },
      offers: {
        '@type': 'Offer',
        price: plugin.pricingModel === 'FREE' ? '0' : undefined,
        priceCurrency: 'USD'
      }
    }))
  }), [plugins]);

  useEffect(() => {
    let scriptTag = document.getElementById('plugins-structured-data') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'plugins-structured-data';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    scriptTag.textContent = JSON.stringify(structuredData);
  }, [structuredData]);

  return (
    <div className="plugins-catalog" itemScope itemType="https://schema.org/ItemList">
      <meta itemProp="name" content="JetBrains Plugins Portfolio" />
      <meta
        itemProp="description"
        content={`${plugins.length} professional IDE plugins for testing, security, accessibility, and development productivity`}
      />

      <div className="plugins-status" role="status" aria-live="polite">
        {dataSource === 'live'
          ? 'Live JetBrains Marketplace data'
          : 'Curated marketplace portfolio shown instantly; live data refreshes when available'}
      </div>

      <div className="plugins-grid" role="list">
        {plugins.map((plugin, index) => {
          const formattedDownloads = plugin.downloads.toLocaleString();
          const pluginUrl = getPluginUrl(plugin);
          const initials = plugin.name
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase())
            .join('');

          return (
            <article
              key={plugin.id}
              className="plugin-card"
              role="listitem"
              itemScope
              itemType="https://schema.org/SoftwareApplication"
              itemProp="itemListElement"
            >
              <meta itemProp="position" content={String(index + 1)} />
              <meta itemProp="applicationCategory" content="DeveloperApplication" />
              <meta itemProp="operatingSystem" content="Windows, macOS, Linux" />

              <a
                href={pluginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="plugin-card-link"
                itemProp="url"
                aria-label={`${plugin.name}: ${plugin.preview}`}
              >
                <div className="plugin-card-header">
                  {plugin.icon ? (
                    <img
                      src={`https://plugins.jetbrains.com${plugin.icon}`}
                      alt={`${plugin.name} icon`}
                      className="plugin-icon"
                      itemProp="image"
                      width="48"
                      height="48"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span className="plugin-icon plugin-icon-fallback" aria-hidden="true">
                      {initials}
                    </span>
                  )}

                  <span className="plugin-pricing-badge" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                    <meta itemProp="price" content={plugin.pricingModel === 'FREE' ? '0' : ''} />
                    <meta itemProp="priceCurrency" content="USD" />
                    {plugin.pricingModel}
                  </span>
                </div>

                <div className="plugin-card-content">
                  <h3 className="plugin-title" itemProp="name">{plugin.name}</h3>
                  <p className="plugin-preview" itemProp="description">{plugin.preview}</p>

                  <div className="plugin-meta">
                    <span
                      className="plugin-downloads"
                      itemProp="interactionStatistic"
                      itemScope
                      itemType="https://schema.org/InteractionCounter"
                    >
                      <meta itemProp="interactionType" content="https://schema.org/DownloadAction" />
                      <span itemProp="userInteractionCount">{formattedDownloads}</span> downloads
                    </span>

                    <div className="plugin-tags" role="list" aria-label="Plugin categories">
                      {plugin.tags.slice(0, 3).map((tag) => (
                        <span
                          key={`${plugin.id}-${tag}`}
                          className="plugin-tag"
                          role="listitem"
                          itemProp="keywords"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </a>
            </article>
          );
        })}
      </div>
    </div>
  );
}
